// src/pages/PrivacyPolicy.js
import React, { useMemo, useState } from "react";
import "./styles/terms.css"; // reuse same layout + styles

const PrivacyPolicy = () => {
  const sections = useMemo(
    () => [
      {
        id: "intro",
        num: 1,
        label: "INTRODUCTION",
        blocks: [
          { h: "Privacy Policy", p: "Welcome to Kupto." },
          {
            h: "",
            p: (
              <>
                This Privacy Policy explains how Kupto (“we”, “us”, “our”) collects,
                uses, discloses, and protects your information when you use our
                website{" "}
                <a href="http://www.kupto.co" target="_blank" rel="noreferrer">
                  www.kupto.co
                </a>{" "}
                and our services.
              </>
            ),
          },
          {
            h: "",
            p: `By using our website/services, you agree to the terms of this Privacy Policy.`,
          },
        ],
      },
      {
        id: "info",
        num: 2,
        label: "INFORMATION WE COLLECT",
        blocks: [
          { h: "1. Information We Collect", p: "" },
          {
            list: [
              {
                b: "Personal Information",
                t: "Name, email address, phone number, delivery address, and billing details provided during account creation or checkout.",
              },
              {
                b: "Order Information",
                t: "Products purchased, order history, payment status, and delivery details.",
              },
              {
                b: "Usage Data",
                t: "Pages visited, actions taken on the site, device information, browser type, and IP address.",
              },
              {
                b: "Communication Data",
                t: "Messages you send to us (email, forms, chats) and your preferences.",
              },
            ],
          },
        ],
      },
      {
        id: "use",
        num: 3,
        label: "HOW WE USE YOUR INFO",
        blocks: [
          { h: "2. How We Use Your Information", p: "" },
          {
            list: [
              { t: "To process orders, payments, deliveries, returns, and refunds." },
              { t: "To create and manage your account and provide customer support." },
              { t: "To improve our website, services, and user experience." },
              { t: "To send order updates, service notices, and important account messages." },
              { t: "To prevent fraud, protect security, and comply with legal obligations." },
            ],
          },
        ],
      },
      {
        id: "sharing",
        num: 4,
        label: "SHARING & DISCLOSURE",
        blocks: [
          { h: "3. Sharing and Disclosure", p: "" },
          {
            list: [
              {
                b: "Service Providers",
                t: "We may share information with trusted third parties who help us operate the website and deliver services (e.g., payments and delivery partners).",
              },
              {
                b: "Legal Requirements",
                t: "We may disclose information if required by law or to protect our rights, users, and safety.",
              },
              {
                b: "Business Transfers",
                t: "If Kupto is involved in a merger, acquisition, or sale, user information may be transferred as part of that transaction.",
              },
            ],
          },
        ],
      },
      {
        id: "cookies",
        num: 5,
        label: "COOKIES",
        blocks: [
          { h: "4. Cookies", p: "" },
          {
            list: [
              { t: "We may use cookies and similar technologies to remember preferences and improve your experience." },
              { t: "You can control cookies through your browser settings. Disabling cookies may affect site functionality." },
            ],
          },
        ],
      },
      {
        id: "security",
        num: 6,
        label: "DATA SECURITY",
        blocks: [
          { h: "5. Data Security", p: "" },
          {
            list: [
              { t: "We use reasonable administrative and technical measures to protect your information." },
              { t: "No method of transmission or storage is 100% secure, so we cannot guarantee absolute security." },
            ],
          },
        ],
      },
      {
        id: "rights",
        num: 7,
        label: "YOUR RIGHTS",
        blocks: [
          { h: "6. Your Rights", p: "" },
          {
            list: [
              { t: "You may request access, correction, or deletion of your personal information where applicable." },
              { t: "You may opt out of marketing communications at any time." },
            ],
          },
        ],
      },
      {
        id: "children",
        num: 8,
        label: "CHILDREN'S PRIVACY",
        blocks: [
          { h: "7. Children’s Privacy", p: "" },
          {
            list: [
              { t: "Our services are not intended for children under 13." },
              { t: "We do not knowingly collect personal information from children. If you believe a child has provided data, contact us." },
            ],
          },
        ],
      },
      {
        id: "changes",
        num: 9,
        label: "CHANGES TO THIS POLICY",
        blocks: [
          { h: "8. Changes to This Privacy Policy", p: "" },
          {
            list: [
              { t: "We may update this policy from time to time." },
              { t: "Changes are effective immediately upon posting on the website." },
              { t: "Your continued use of the website after updates means you accept the updated policy." },
            ],
          },
        ],
      },
      {
        id: "contact",
        num: 10,
        label: "CONTACT US",
        blocks: [
          { h: "Contact Us", p: "" },
          {
            list: [
              {
                t: (
                  <>
                    If you have questions about this Privacy Policy, contact us at{" "}
                    <a href="mailto:kupto2020@gmail.com">kupto2020@gmail.com</a>.
                  </>
                ),
              },
            ],
          },
        ],
      },
    ],
    []
  );

  const [active, setActive] = useState("intro");

  const goTo = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderBlock = (b, idx) => {
    if (b.list) {
      return (
        <ul className="tc-ul" key={idx}>
          {b.list.map((li, i) => (
            <li key={i} className="tc-li">
              {li.b ? (
                <>
                  <strong>{li.b}:</strong> {li.t}
                </>
              ) : (
                li.t
              )}
            </li>
          ))}
        </ul>
      );
    }

    if (!b.h) {
      return (
        <p className="tc-p" key={idx}>
          {b.p}
        </p>
      );
    }

    return (
      <div key={idx}>
        <h2 className="tc-h2">{b.h}</h2>
        {b.p ? <p className="tc-p">{b.p}</p> : null}
      </div>
    );
  };

  return (
    <div className="tc-page">
      <div className="tc-shell">
        {/* LEFT NAV */}
        <aside className="tc-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`tc-navItem ${active === s.id ? "active" : ""}`}
              onClick={() => goTo(s.id)}
            >
              <span className="tc-dot">{s.num}</span>
              <span className="tc-navText">{s.label}</span>
            </button>
          ))}
        </aside>

        {/* MAIN */}
        <main className="tc-main">
          <header className="tc-header">
            <h1 className="tc-title">Privacy Policy</h1>
          </header>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="tc-section">
              <div className="tc-topLabel">{s.label}</div>
              {s.blocks?.map((b, idx) => renderBlock(b, idx))}

              {s.id === "intro" && (
                <div className="tc-nextRow">
                  <span className="tc-readNext">Read next:</span>
                  <button className="tc-pill" type="button" onClick={() => goTo("info")}>
                    INFORMATION WE COLLECT
                  </button>
                </div>
              )}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
